import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Select, Input, Alert, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  rejectionService,
  type RejectionPayload,
  type RejectionReasonDefinitionDTO,
  MIN_REJECTION_REMARKS_LENGTH,
} from '@/services/rejection.service';

const { Text } = Typography;

export interface RejectionReasonModalProps {
  open: boolean;
  title?: string;
  /** Universal approval `entity_type` or domain document type key expected by the API. */
  documentType: string;
  documentLabel?: string;
  confirmLoading?: boolean;
  onCancel: () => void;
  onConfirm: (payload: RejectionPayload) => void | Promise<void>;
}

function remarksRequiredFor(def: RejectionReasonDefinitionDTO | undefined): boolean {
  if (!def) return false;
  return def.requires_additional_remarks || def.is_other_bucket || def.reason_code === 'OTHER';
}

export const RejectionReasonModal: React.FC<RejectionReasonModalProps> = ({
  open,
  title = 'Reject',
  documentType,
  documentLabel,
  confirmLoading,
  onCancel,
  onConfirm,
}) => {
  const [form] = Form.useForm<{ reason_code: string; remarks: string }>();

  const { data: reasons = [], isLoading } = useQuery({
    queryKey: ['rejection-reasons', documentType],
    queryFn: () => rejectionService.listReasonsForDocument(documentType),
    enabled: open && !!documentType,
  });

  const reasonCode = Form.useWatch('reason_code', form);

  const selectedDef = useMemo(
    () => reasons.find((r) => r.reason_code === reasonCode),
    [reasons, reasonCode]
  );

  const needRemarks = remarksRequiredFor(selectedDef);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleOk = async () => {
    const v = await form.validateFields();
    const code = (v.reason_code ?? '').trim();
    if (!code) return;
    const remarks = (v.remarks ?? '').trim();
    if (needRemarks && remarks.length < MIN_REJECTION_REMARKS_LENGTH) {
      form.setFields([
        {
          name: 'remarks',
          errors: [`Enter at least ${MIN_REJECTION_REMARKS_LENGTH} characters for this reason`],
        },
      ]);
      return;
    }
    await onConfirm({
      rejection_reason_code: code,
      remarks: remarks.length ? remarks : undefined,
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      confirmLoading={confirmLoading}
      okText="Reject"
      okButtonProps={{ danger: true }}
      destroyOnClose
      width={520}
    >
      {documentLabel ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {documentLabel}
        </Text>
      ) : null}
      {!isLoading && reasons.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="No rejection reasons are configured for this document type. Ask an administrator to add reasons under Platform → Rejection reasons."
        />
      ) : null}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="reason_code"
          label="Rejection reason"
          rules={[{ required: true, message: 'Select a rejection reason' }]}
        >
          <Select
            loading={isLoading}
            placeholder="Select reason"
            showSearch
            optionFilterProp="label"
            options={reasons.map((r) => ({
              value: r.reason_code,
              label: `${r.name} (${r.reason_code})`,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="remarks"
          label={needRemarks ? `Additional remarks (required, min ${MIN_REJECTION_REMARKS_LENGTH} chars)` : 'Remarks (optional)'}
          rules={
            needRemarks
              ? [
                  { required: true, message: 'Remarks are required for this reason' },
                  {
                    min: MIN_REJECTION_REMARKS_LENGTH,
                    message: `At least ${MIN_REJECTION_REMARKS_LENGTH} characters`,
                  },
                ]
              : []
          }
        >
          <Input.TextArea rows={3} placeholder="Context for the submitter" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
